import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import UnscheduledJobsPicker from './UnscheduledJobsPicker';
import DatePickerWithValidation from './DatePickerWithValidation';
import CrewSelector from './CrewSelector';
import DurationOverride from './DurationOverride';
import ConflictWarnings from './ConflictWarnings';
import BatchAssignment from './BatchAssignment';
import { buildScheduleUpdate, markJobOrderScheduled } from '../../utils/schedule-job';
import { useJobs } from '../../hooks/useJobs';
import { generateWorkdays } from '../../utils/work-day-calculator';
import { isoDateString, dateFromInput, dateToInput } from '../../utils/date-utils';
import { isAssignable } from '../../utils/job-status';

// presetJob: opened by dropping a job from the calendar's Unscheduled panel
export default function AssignJobSheet({ isOpen, onClose, presetDate = null, presetJob = null }) {
  const { jobs, updateJob } = useJobs();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [selectedJob, setSelectedJob] = useState(null);
  const [startDate, setStartDate] = useState(presetDate ? dateToInput(presetDate) : '');
  const [assignedCrew, setAssignedCrew] = useState('');
  const [duration, setDuration] = useState(1);
  const [batchMode, setBatchMode] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  // Jobs that can be put on the calendar: in production, ready for install, or on hold
  const unscheduledJobs = jobs.filter(isAssignable);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedJob(presetJob);
      setStartDate(presetDate ? dateToInput(presetDate) : '');
      setAssignedCrew('');
      setDuration(presetJob?.durationDays || 1);
      setBatchMode(false);
      setConflicts([]);
      setErrors({});
    }
  }, [isOpen, presetDate, presetJob]);

  // Update duration when job is selected
  useEffect(() => {
    if (selectedJob) {
      setDuration(selectedJob.durationDays || 1);
    }
  }, [selectedJob]);

  // Check for scheduling conflicts when date or crew changes
  useEffect(() => {
    if (startDate && selectedJob) {
      checkConflicts();
    }
  }, [startDate, assignedCrew, duration, selectedJob]);

  function checkConflicts() {
    if (!startDate) return;

    // Generate work days for this job
    const workDays = generateWorkdays(dateFromInput(startDate), duration);
    const workDayStrings = workDays.map(d => isoDateString(d));

    // Find jobs that overlap with these dates
    const overlapping = jobs.filter(job => {
      if (!job.scheduledDate || job.id === selectedJob?.id) return false;
      
      // Check if crew matches (if crew is assigned)
      if (assignedCrew && job.assignedCrew !== assignedCrew) return false;

      const jobDates = generateWorkdays(new Date(job.scheduledDate), job.durationDays || 1);
      const jobDateStrings = jobDates.map(d => isoDateString(d));

      // Check for overlap
      return workDayStrings.some(date => jobDateStrings.includes(date));
    });

    setConflicts(overlapping);
  }

  function handleJobSelect(job) {
    setSelectedJob(job);
    setDuration(job.durationDays || 1);
    setErrors({});
  }

  function validate() {
    const newErrors = {};

    if (!selectedJob) {
      newErrors.job = 'Please select a job to schedule';
    }

    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (duration < 1) {
      newErrors.duration = 'Duration must be at least 1 day';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleAssign() {
    if (!validate()) return;

    setSaving(true);

    try {
      await updateJob(selectedJob.id, buildScheduleUpdate(selectedJob, dateFromInput(startDate), duration, assignedCrew));
      console.log('✅ Job scheduled successfully');
      markJobOrderScheduled(selectedJob.id);

      onClose();
    } catch (error) {
      console.error('❌ Error scheduling job:', error);
      setErrors({ submit: 'Failed to schedule job. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Job to Schedule"
      size="large"
    >
      <div style={{
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '24px'
      }}>
        {/* Info message if no jobs ready */}
        {unscheduledJobs.length === 0 && (
          <div style={{
            padding: '16px',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            color: 'var(--status-text-bold-blue)',
            fontSize: '14px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            ℹ️ No jobs are ready for installation. Jobs must be &quot;In Production&quot;, &quot;Ready for Install&quot;, or &quot;On Hold&quot; to be scheduled.
          </div>
        )}

        {/* Batch Mode Toggle */}
        <BatchAssignment
          enabled={batchMode}
          onToggle={setBatchMode}
        />

        {/* Job Picker */}
        <div style={{ marginBottom: '20px' }}>
          <UnscheduledJobsPicker
            jobs={unscheduledJobs}
            selectedJobId={selectedJob?.id}
            onSelect={handleJobSelect}
            error={errors.job}
          />
        </div>

        {selectedJob && (
          <>
            <div style={{
              height: '1px',
              background: 'var(--cell-border)',
              margin: '24px 0'
            }} />

            {/* Conflict Warnings */}
            <ConflictWarnings conflicts={conflicts} />

            {/* Start Date */}
            <div style={{ marginBottom: '20px' }}>
              <DatePickerWithValidation
                value={startDate}
                onChange={setStartDate}
                error={errors.startDate}
              />
            </div>

            {/* Duration Override */}
            <div style={{ marginBottom: '20px' }}>
              <DurationOverride
                value={duration}
                defaultValue={selectedJob.durationDays || 1}
                onChange={setDuration}
                error={errors.duration}
              />
            </div>

            {/* Crew Selector */}
            <div style={{ marginBottom: '20px' }}>
              <CrewSelector
                value={assignedCrew}
                onChange={setAssignedCrew}
                error={errors.crew}
              />
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div style={{
                padding: '12px',
                background: 'var(--error-bg)',
                border: '1px solid var(--error-border)',
                borderRadius: '8px',
                color: 'var(--status-text-bold-red)',
                fontSize: '13px',
                marginBottom: '16px'
              }}>
                {errors.submit}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid var(--cell-border)',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        background: 'var(--bg-secondary)'
      }}>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleAssign}
          disabled={saving || !selectedJob}
        >
          {saving ? 'Scheduling...' : 'Schedule Job'}
        </Button>
      </div>
    </Modal>
  );
}